// src/pages/admin/AiConsumption.jsx
// Superadmin AI consumption journal: per-company token usage + estimated cost
// across every AI operation (chatbot + Ateliers 1-5 + synthesis).
// Layout cloned from ActivityLogs.jsx (filters, table, expandable rows, pagination).
import React, { useState, useEffect, useCallback } from 'react';
import { companyService } from '../../services/apiServices';
import aiConsumptionService, { AI_OPERATION_LABELS } from '../../services/aiConsumptionService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showErrorToast } from '../../utils/toastUtils';
import {
  Search, Download, RefreshCw, Cpu, Coins, Hash, Building2,
  ChevronUp, ChevronDown, AlertTriangle, CheckCircle2, Zap
} from 'lucide-react';

const PROVIDERS = [
  { value: '', label: 'Tous les providers' },
  { value: 'gemini', label: 'Gemini (cloud)' },
  { value: 'ollama', label: 'Ollama (local, gratuit)' },
  { value: 'openrouter', label: 'OpenRouter' }
];

const OPERATIONS = [
  { value: '', label: 'Toutes les opérations' },
  ...Object.entries(AI_OPERATION_LABELS).map(([value, label]) => ({ value, label }))
];

const formatTokens = (n) => (n || 0).toLocaleString('fr-FR');
const formatCost = (n) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} $`;

const AiConsumption = () => {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState({
    totals: { tokensIn: 0, tokensOut: 0, totalTokens: 0, estimatedCost: 0, requests: 0, failed: 0 },
    byCompany: [],
    byModel: [],
    byDay: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const [filters, setFilters] = useState({
    search: '',
    companyId: '',
    provider: '',
    operation: '',
    startDate: '',
    endDate: ''
  });

  const [pagination, setPagination] = useState({
    page: 1, limit: 20, total: 0, pages: 0
  });

  const activeParams = useCallback((page) => {
    const params = { page, limit: pagination.limit, sort: '-createdAt' };
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params[key] = filters[key];
    });
    return params;
  }, [pagination.limit, filters]);

  const fetchRows = useCallback(async (page = 1, fetchBackground = false) => {
    if (!fetchBackground) setIsLoading(true);
    setIsRefreshing(true);
    try {
      const response = await aiConsumptionService.getConsumption(activeParams(page));
      if (response.success) {
        setRows(response.data || []);
        if (response.pagination) setPagination(response.pagination);
      }
      const summaryRes = await aiConsumptionService.getSummary(activeParams(1));
      if (summaryRes.success && summaryRes.data) setSummary(summaryRes.data);
    } catch (error) {
      showErrorToast('Erreur lors du chargement de la consommation IA.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeParams]);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const compRes = await companyService.getCompanies({ limit: 1000 });
        if (compRes.success) {
          setCompanies([{ id: '', name: 'Toutes les entités' }, ...(compRes.data || [])]);
        }
      } catch (e) {
        console.warn('Échec du chargement des entités', e);
      }
    };
    loadMetadata();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters((prev) => ({ ...prev, search: searchInput }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search]);

  useEffect(() => {
    if (!isLoading) fetchRows(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCsv = async () => {
    if (rows.length === 0) return showErrorToast('Aucune donnée à exporter');
    setIsExporting(true);
    try {
      await aiConsumptionService.exportCsv(activeParams(1));
    } catch (error) {
      showErrorToast("Erreur lors de l'export CSV.");
    } finally {
      setIsExporting(false);
    }
  };

  const maxDayTokens = Math.max(1, ...summary.byDay.map((d) => d.tokens || 0));
  const lastDays = summary.byDay.slice(-14);

  return (
    <div className="p-4 md:p-8 max-w-full mx-auto bg-gray-50/50 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <Cpu className="w-8 h-8 text-blue-700" />
            Consommation IA
          </h1>
          <p className="text-gray-500 mt-1.5 text-sm font-medium">
            Journal multi-tenant de tous les appels IA (chatbot + Ateliers) : tokens et coût estimé
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExportCsv}
            disabled={rows.length === 0 || isExporting}
            className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-50 hover:text-blue-600 focus:ring-4 focus:ring-gray-100 transition-all font-medium text-sm shadow-sm disabled:opacity-50"
          >
            <Download size={18} />
            Exporter (CSV)
          </button>
          <button
            onClick={() => fetchRows(1)}
            disabled={isRefreshing}
            className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-200 transition-all font-medium text-sm shadow-sm"
          >
            <RefreshCw size={18} className={isRefreshing && !isLoading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Tokens totaux', value: formatTokens(summary.totals.totalTokens), sub: `${formatTokens(summary.totals.tokensIn)} in / ${formatTokens(summary.totals.tokensOut)} out`, icon: Hash, color: 'blue' },
          { label: 'Coût estimé', value: formatCost(summary.totals.estimatedCost), sub: 'USD, tarifs au moment de l\u2019appel', icon: Coins, color: 'emerald' },
          { label: 'Requêtes', value: formatTokens(summary.totals.requests), sub: `${formatTokens(summary.totals.failed)} en échec`, icon: Zap, color: 'indigo' },
          { label: 'Top entité', value: summary.byCompany[0] ? (summary.byCompany[0].name || '—') : '—', sub: summary.byCompany[0] ? `${formatTokens(summary.byCompany[0].tokens)} tokens` : 'Aucune donnée', icon: Building2, color: 'rose' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-${stat.color}-50 text-${stat.color}-600`}>
              <stat.icon size={24} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1 truncate">{stat.value}</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{stat.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {lastDays.length > 0 && (
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Tokens par jour (14 derniers jours)</p>
          <div className="flex items-end gap-1.5 h-24">
            {lastDays.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date} : ${formatTokens(d.tokens)} tokens`}>
                <div
                  className="w-full bg-blue-600/80 hover:bg-blue-600 rounded-t transition-all"
                  style={{ height: `${Math.max(4, Math.round(((d.tokens || 0) / maxDayTokens) * 88))}px` }}
                />
                <span className="text-[10px] text-gray-400 font-mono">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <div className="col-span-1 border-r-0 md:border-r border-gray-100 md:col-span-3 md:pr-4">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Modèle, opération..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all"
              />
            </div>
          </div>

          <div className="col-span-1 md:col-span-9 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block flex items-center gap-1.5"><Building2 size={12} /> Entité</label>
              <select
                value={filters.companyId}
                onChange={(e) => setFilters((p) => ({ ...p, companyId: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all appearance-none cursor-pointer truncate"
              >
                {companies.map((c) => <option key={c.id || c._id || 'all'} value={c.id || c._id || ''}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Provider</label>
              <select
                value={filters.provider}
                onChange={(e) => setFilters((p) => ({ ...p, provider: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all appearance-none cursor-pointer"
              >
                {PROVIDERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Opération</label>
              <select
                value={filters.operation}
                onChange={(e) => setFilters((p) => ({ ...p, operation: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all appearance-none cursor-pointer"
              >
                {OPERATIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Dès le</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Jusqu'au</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
                className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-600 text-sm font-medium transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
          <LoadingSpinner />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-4 bg-gray-50 rounded-full mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Aucune consommation enregistrée</h3>
          <p className="text-gray-500 mt-1 max-w-sm text-center text-sm">
            Aucun appel IA ne correspond à ces critères. L'historique Atelier antérieur au journal n'est pas repris (chatbot uniquement, via backfill).
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-40">Date</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-44">Entité</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Opération</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Modèle</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Tokens</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Coût est.</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-16 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const isExpanded = expandedRow === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`hover:bg-slate-50 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : row.id)}
                      >
                        <td className="py-3 px-6 whitespace-nowrap">
                          <div className="text-xs text-gray-500 font-mono">
                            {row.createdAt ? new Date(row.createdAt).toLocaleString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            }) : '—'}
                          </div>
                        </td>
                        <td className="py-3 px-6">
                          <span className="text-sm font-semibold text-indigo-900 truncate block max-w-[150px]">
                            {row.companyName || '—'}
                          </span>
                        </td>
                        <td className="py-3 px-6">
                          <span className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide border rounded-md w-max bg-blue-50 text-blue-700 border-blue-200">
                            {AI_OPERATION_LABELS[row.operation] || row.operation}
                          </span>
                        </td>
                        <td className="py-3 px-6">
                          <div className="text-xs font-mono text-gray-700 truncate max-w-[180px]">{row.model}</div>
                          <div className="text-[10px] text-gray-400 uppercase">{row.provider}{row.isFallback ? ' · fallback' : ''}</div>
                        </td>
                        <td className="py-3 px-6 text-right">
                          <div className="text-sm font-bold text-gray-900 font-mono">{formatTokens(row.totalTokens)}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{formatTokens(row.tokensIn)} in / {formatTokens(row.tokensOut)} out</div>
                        </td>
                        <td className="py-3 px-6 text-right">
                          <div className="text-sm font-bold text-gray-900 font-mono">{formatCost(row.estimatedCost)}</div>
                          {!row.priced && <div className="text-[10px] text-amber-600">tarif inconnu</div>}
                          {row.tokensUnknown && <div className="text-[10px] text-amber-600">usage non remonté</div>}
                        </td>
                        <td className="py-3 px-6 text-center">
                          <span className="inline-flex items-center">
                            {row.status === 'failed'
                              ? <AlertTriangle size={14} className="text-rose-600" />
                              : <CheckCircle2 size={14} className="text-emerald-500" />}
                            <button className="ml-2 p-1 rounded bg-white border shadow-sm text-gray-500 transition-colors">
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </span>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-slate-900 border-t border-slate-800">
                          <td colSpan="7" className="p-0">
                            <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
                              <div>
                                <span className="text-[10px] text-slate-500 block uppercase">Utilisateur</span>
                                <span className="text-slate-300 break-all">{row.userEmail || row.userId || '—'}</span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-500 block uppercase">Analyse</span>
                                <span className="text-slate-300 break-all">{row.analysisId || '—'}</span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-500 block uppercase">Latence</span>
                                <span className="text-slate-300">{row.latencyMs} ms</span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-500 block uppercase">Statut</span>
                                <span className="text-slate-300">{row.status}{row.errorCode ? ` · ${row.errorCode}` : ''}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.pages > 0 && (
            <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">
                Affichage de {(pagination.page - 1) * pagination.limit + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} enregistrements
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchRows(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 transition-colors"
                >
                  Précédent
                </button>
                <div className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold shadow-inner">
                  {pagination.page} / {pagination.pages}
                </div>
                <button
                  onClick={() => fetchRows(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AiConsumption;
